import { useCallback, useEffect, useRef, useState } from 'react'

import API from '../../component/Axios'
import { createListLinkTarget } from '../../utils/listPageState'
import {
    clearAppPopupDraft,
    createAppPopupDraftPayload,
    saveAppPopupDraft,
} from './appPopupDraftUtils'
import {
    APP_POPUP_IMAGE_FILE_ERROR_MESSAGE,
    APP_POPUP_IMAGE_LEAVE_MESSAGE,
    APP_POPUP_IMAGE_PREVIEW_ERROR_MESSAGE,
    chooseAppPopupProduct,
    createAppPopupFormData,
    getAppPopupValidationMessage,
    hasPendingAppPopupImage,
    isAppPopupImageFile,
    readAppPopupImagePreview,
    shouldShowAppPopupProductPicker,
} from './appPopupFormUtils'

const PRODUCT_PICKER_OFFSET = 5

const normalizePickerPage = (page) => {
    const parsedPage = Number(page)

    return parsedPage > 0 ? parsedPage : 1
}

const normalizeSearchText = (searchText) => {
    return searchText || ''
}

// 업로드 파일은 브라우저 보안상 복구할 수 없어서, 새로고침/탭 닫기 직전에만 기본 경고를 띄운다.
const usePendingAppPopupImageUnloadGuard = (enabled) => {
    useEffect(() => {
        if (typeof window === 'undefined' || !enabled) {
            return undefined
        }

        const handleBeforeUnload = (event) => {
            event.preventDefault()
            event.returnValue = ''
            return ''
        }

        window.addEventListener('beforeunload', handleBeforeUnload)

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [enabled])
}

// App 랜덤 팝업 폼의 draft, 이미지, 상품 선택, 저장, 이탈 경고 흐름을 한 곳에서 관리한다.
export default function useAppPopupFormFlow({
    draftKey,
    draftEnabled,
    params,
    setParams,
    setImage,
    saving,
    setSaving,
    history,
    listState,
    saveConfig,
    pickerConfig,
    showError,
}) {
    const {
        search: listSearch,
        curPage: listCurPage,
        searchSelect: listSearchSelect,
    } = listState
    const {
        endpoint,
        popupId,
        failureMessage,
    } = saveConfig
    const {
        initialPage,
        initialSearchText,
        initialSearchSelect,
    } = pickerConfig
    const initialDraftSnapshotRef = useRef('')
    const searchProductTitleRef = useRef(normalizeSearchText(initialSearchText))
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [curPage, setCurPage] = useState(() => normalizePickerPage(initialPage))
    const [productDatas, setProductDatas] = useState([])
    const [searchProductTitle, setSearchProductTitle] = useState(() => normalizeSearchText(initialSearchText))
    const [searchSelect] = useState({
        keyword1: initialSearchSelect?.keyword1 || '',
    })
    const productPickerVisible = shouldShowAppPopupProductPicker(params.type)
    const leaveGuardEnabled = hasPendingAppPopupImage(params) && !saving && !navigationUnlocked

    usePendingAppPopupImageUnloadGuard(leaveGuardEnabled)

    const setInitialDraftSnapshot = useCallback(({
        params: initialParams = {},
        pickerPage: initialPickerPage = 1,
        pickerSearchText: initialPickerSearchText = '',
    } = {}) => {
        initialDraftSnapshotRef.current = JSON.stringify(createAppPopupDraftPayload({
            params: initialParams,
            pickerPage: initialPickerPage,
            pickerSearchText: initialPickerSearchText,
        }))
    }, [])

    const restorePickerState = useCallback(({ page, searchText } = {}) => {
        const nextSearchText = normalizeSearchText(searchText)

        searchProductTitleRef.current = nextSearchText
        setSearchProductTitle(nextSearchText)
        setCurPage(normalizePickerPage(page))
    }, [])

    useEffect(() => {
        if (!draftEnabled || !initialDraftSnapshotRef.current) {
            return
        }

        const nextDraft = createAppPopupDraftPayload({
            params,
            pickerPage: curPage,
            pickerSearchText: searchProductTitle,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppPopupDraft(draftKey)
            return
        }

        saveAppPopupDraft(draftKey, nextDraft)
    }, [curPage, draftEnabled, draftKey, params, searchProductTitle])

    const getProductData = useCallback(async () => {
        try {
            const { data } = await API.post('/api/v1/admin/appPopup/getProductList', {
                page: curPage,
                offset: PRODUCT_PICKER_OFFSET,
                searchText: searchProductTitleRef.current,
                searchSelect,
            })

            if (data.code === 200) {
                setTotalCount(data.data.count)
                setProductDatas(data.data.rows)
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                showError(data.message)
            }
        } catch (_error) {
            showError('애니멀런 상품 목록을 불러오지 못했습니다.')
        }
    }, [curPage, history, searchSelect, showError])

    useEffect(() => {
        if (!productPickerVisible) {
            return
        }

        getProductData()
    }, [getProductData, productPickerVisible])

    const handleSearchProductTitleChange = useCallback((event) => {
        searchProductTitleRef.current = event.target.value
        setSearchProductTitle(event.target.value)
    }, [])

    const searchProduct = useCallback(() => {
        if (curPage === 1) {
            getProductData()
            return
        }

        setCurPage(1)
    }, [curPage, getProductData])

    const chooseProduct = useCallback((meetId) => {
        setParams((prev) => chooseAppPopupProduct(prev, meetId))
    }, [setParams])

    const handleUpdateField = useCallback((event) => {
        const { name, value } = event.target

        if (name === 'type' && shouldShowAppPopupProductPicker(value)) {
            setCurPage(1)
        }

        setParams((prev) => ({
            ...prev,
            [name]: value,
        }))
    }, [setParams])

    const handleUploadClick = useCallback(async (event) => {
        event.preventDefault()

        const file = event.target.files?.[0]
        const fieldName = event.target.name
        if (!file) {
            return
        }

        if (!isAppPopupImageFile(file)) {
            event.target.value = ''
            showError(APP_POPUP_IMAGE_FILE_ERROR_MESSAGE)
            return
        }

        try {
            const nextImage = await readAppPopupImagePreview(file)
            setParams((prev) => ({
                ...prev,
                [fieldName]: file,
            }))
            setImage(nextImage)
        } catch (_error) {
            showError(APP_POPUP_IMAGE_PREVIEW_ERROR_MESSAGE)
        }
    }, [setImage, setParams, showError])

    const moveToList = useCallback((snackbarState) => {
        setNavigationUnlocked(true)
        clearAppPopupDraft(draftKey)

        // 저장 후 이동은 이탈 경고를 잠시 풀고, 목록 query는 그대로 유지한다.
        setTimeout(() => {
            history.push(createListLinkTarget({
                pathname: '/appPopups',
                search: listSearch,
                curPage: listCurPage,
                searchSelect: listSearchSelect,
                ...(snackbarState ? {
                    openSnackbar: true,
                    severitySnackbar: snackbarState.severity,
                    messageSnackbar: snackbarState.message,
                } : {}),
            }))
        }, 0)
    }, [draftKey, history, listCurPage, listSearch, listSearchSelect])

    const handleSubmit = useCallback(async (event) => {
        event.preventDefault()

        if (saving) {
            return
        }

        const validationMessage = getAppPopupValidationMessage(params)
        if (validationMessage) {
            showError(validationMessage)
            return
        }

        try {
            setSaving(true)
            const { data } = await API({
                method: 'post',
                url: endpoint,
                data: createAppPopupFormData(params, { popupId }),
                headers: { "Content-Type": "multipart/form-data" },
            })

            if (data.code === 200) {
                moveToList({
                    severity: 'success',
                    message: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                showError(data.message)
            }
        } catch (_error) {
            showError(failureMessage)
        } finally {
            setSaving(false)
        }
    }, [
        endpoint,
        failureMessage,
        history,
        moveToList,
        params,
        popupId,
        saving,
        setSaving,
        showError,
    ])

    const handleBackClick = useCallback((event) => {
        if (!leaveGuardEnabled) {
            clearAppPopupDraft(draftKey)
            return
        }

        event.preventDefault()

        if (!window.confirm(APP_POPUP_IMAGE_LEAVE_MESSAGE)) {
            return
        }

        moveToList()
    }, [draftKey, leaveGuardEnabled, moveToList])

    return {
        setInitialDraftSnapshot,
        restorePickerState,
        leaveGuardEnabled,
        moveToList,
        productPicker: {
            visible: productPickerVisible,
            totalCount,
            curPage,
            setCurPage,
            offset: PRODUCT_PICKER_OFFSET,
            productDatas,
            searchProductTitle,
            onSearchProductTitleChange: handleSearchProductTitleChange,
            onSearch: searchProduct,
            onChooseProduct: chooseProduct,
        },
        handlers: {
            onSubmit: handleSubmit,
            onImageChange: handleUploadClick,
            onFieldChange: handleUpdateField,
            onBackClick: handleBackClick,
        },
    }
}
